import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { config } from '../config.ts';
import { IconButton } from '../ui/IconButton.tsx';
import { Markdown } from './markdown.tsx';

const TITLES = { privacy: 'Privacy Policy', terms: 'Terms of Service' } as const;

/** Public /privacy and /terms: Markdown from web/public/legal/<doc>.md (no sign-in needed). */
export function LegalScreen({ doc }: { doc: keyof typeof TITLES }) {
  const navigate = useNavigate();
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    document.title = `${TITLES[doc]} – Better Lights`;
    let alive = true;
    setText(null);
    setFailed(false);
    fetch(`/legal/${doc}.md`, { cache: 'no-cache' })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => alive && setText(t.replaceAll('<SUPPORT_EMAIL>', config.supportEmail)))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      document.title = 'Better Lights';
    };
  }, [doc]);

  return (
    <main
      className="min-h-dvh"
      style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'calc(var(--safe-bottom) + 32px)' }}
    >
      <div className="flex min-h-11 items-center gap-2 px-2">
        <IconButton
          icon={ArrowLeft}
          label="Back"
          onClick={() => (history.length > 1 ? history.back() : void navigate({ to: '/' }))}
        />
        <span className="t-title">{TITLES[doc]}</span>
      </div>
      <article className="mx-auto flex max-w-[720px] flex-col gap-4 p-4" aria-label={TITLES[doc]}>
        {text != null ? (
          <Markdown source={text} />
        ) : failed ? (
          <p className="t-body text-dim">This page can’t be loaded right now. Check your connection and try again.</p>
        ) : (
          <p className="t-body text-dim">Loading…</p>
        )}
        <nav className="t-caption mt-6 flex flex-wrap gap-4 border-t border-border pt-4 text-muted" aria-label="Legal">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/delete-account">Delete your account</Link>
        </nav>
      </article>
    </main>
  );
}
